// ── Tournament Panel ── Full-screen overlay for tournament management
// Lists tournaments, create/register/start, view bracket.
// Fetches from /api/tournament/* endpoints.

import { getToken } from '../auth/token-store';
import { isMobile } from '../core/device';

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';
const MOBILE_FRIENDLY_4COL_GRID =
  '1fr minmax(60px, auto) minmax(60px, auto) minmax(60px, auto)';

// ── Types matching server JSON shapes ──

export type TournamentFormat = 'single_elim' | 'double_elim';
export type TournamentStatus = 'registration' | 'in_progress' | 'completed';

export interface TournamentTeam {
  id: string;
  name: string;
  player_ids: string[];
  seed: number;
}

export interface BracketMatch {
  round: number;
  match_num: number;
  team1_id: string;
  team2_id: string;
  winner_id: string;
  score1: number;
  score2: number;
  status: 'pending' | 'completed';
}

export interface TournamentInfo {
  id: string;
  name: string;
  format: TournamentFormat;
  max_teams: number;
  status: TournamentStatus;
  teams: TournamentTeam[];
  bracket: BracketMatch[][] | null;
  created_at: string;
}

/**
 * Lightweight subset returned by GET /api/tournament/list.
 * Server type: `game.TournamentSummary` in server/internal/game/tournament.go.
 * Excludes teams + bracket so the list payload stays small.
 */
export interface TournamentSummary {
  id: string;
  name: string;
  format: TournamentFormat;
  max_teams: number;
  status: TournamentStatus;
  team_count: number;
  created_at: string;
}

export interface TournamentMatchHistoryEntry {
  id: string;
  name: string;
  date: string;
  placement: string;
}

// ── View state ──

type TournamentView = 'list' | 'detail' | 'create' | 'bracket' | 'history';

const DEFAULT_MATCH_HISTORY: TournamentMatchHistoryEntry[] = [
  { id: 'hist-1', name: 'Outpost Open', date: '2026-04-12', placement: '2nd Place' },
  { id: 'hist-2', name: 'Dustline Cup', date: '2026-03-29', placement: 'Semifinalist' },
  { id: 'hist-3', name: 'Mirage Masters', date: '2026-03-02', placement: 'Top 8' },
];

/**
 * Inline-form mode within the detail view. 'none' renders the regular
 * action bar; 'register-team' and 'record-result' replace the action bar
 * with the corresponding form (admin workflows previously driven by
 * window.prompt — U-M-003 follow-up).
 */
type PendingDetailForm = 'none' | 'register-team' | 'record-result';

export class TournamentPanel {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private visible = false;
  private apiBaseUrl: string;
  private onBack: (() => void) | null = null;
  private abortController = new AbortController();
  private activeDialog: HTMLElement | null = null;
  /**
   * UI-M4: render-scope AbortController for per-render listeners on action
   * buttons, list rows, and form-field inputs. Recycled at the top of
   * render() so old DOM closures are released.
   */
  private renderController = new AbortController();

  // Data
  private tournaments: TournamentInfo[] = [];
  private currentTournament: TournamentInfo | null = null;
  private currentBracket: BracketMatch[][] | null = null;
  private view: TournamentView = 'list';
  private errorMessage = '';
  private pendingDetailForm: PendingDetailForm = 'none';
  private bracketZoom = 1;
  private matchHistory: TournamentMatchHistoryEntry[] = DEFAULT_MATCH_HISTORY;

  constructor(apiBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl.replace(/\/$/, '');

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '58',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    this.contentEl = document.createElement('div');
    Object.assign(this.contentEl.style, {
      width: 'min(740px, calc(100vw - 24px))',
      maxHeight: '85vh',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });
    this.overlay.appendChild(this.contentEl);

    // Click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.hide();
    }, { signal: this.abortController.signal });

    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', (e) => this.handleKeyDown(e), {
      signal: this.abortController.signal,
    });
  }

  setOnBack(callback: (() => void) | null): void {
    this.onBack = callback;
  }

  setMatchHistory(history: TournamentMatchHistoryEntry[]): void {
    this.matchHistory = history.map((entry) => ({ ...entry }));
    if (this.visible && this.view === 'history') this.render();
  }

  async show(): Promise<void> {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.errorMessage = '';
    await this.loadTournamentList();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.closeDialog();
    this.overlay.style.display = 'none';
    this.onBack?.();
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.abortController.abort();
    this.renderController.abort();
    this.closeDialog();
    this.overlay.remove();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      if (this.activeDialog) {
        this.closeDialog();
        return;
      }
      this.hide();
    }
  }

  private closeDialog(): void {
    if (!this.activeDialog) return;
    this.activeDialog.remove();
    this.activeDialog = null;
  }

  // ── API helpers ──

  private authHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private async apiFetch(path: string, init?: RequestInit): Promise<Response> {
    return fetch(`${this.apiBaseUrl}${path}`, {
      ...init,
      headers: { ...this.authHeaders(), ...(init?.headers ?? {}) },
    });
  }

  // ── Data loaders ──

  /**
   * U-M-003 follow-up: pull the canonical list from
   * GET /api/tournament/list (lightweight summaries) instead of the
   * old localStorage-tracked ID cache. The cache helpers below are
   * retained for the post-create flow (so `loadTournamentDetail` after
   * a fresh create still warms the most-recent ID) but they are no
   * longer the source of truth for the list view.
   */
  private async loadTournamentList(): Promise<void> {
    this.view = 'list';
    this.pendingDetailForm = 'none';
    this.tournaments = [];
    try {
      const res = await this.apiFetch('/api/tournament/list');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const summaries = await res.json() as TournamentSummary[];
      // Project summary → TournamentInfo for the existing list renderer.
      // Teams and bracket are empty placeholders; the detail view fetches
      // the full record when a user clicks into a tournament.
      this.tournaments = summaries.map((s) => ({
        id: s.id,
        name: s.name,
        format: s.format,
        max_teams: s.max_teams,
        status: s.status,
        teams: [],
        bracket: null,
        created_at: s.created_at,
      }));
      this.errorMessage = '';
    } catch (err) {
      console.error('[TournamentPanel] Failed to load tournament list:', err);
      this.errorMessage = 'Failed to load tournaments';
    }
    this.render();
  }

  private async loadTournamentDetail(tournamentId: string): Promise<void> {
    // Clear any inline form state when switching tournaments so stale
    // form data doesn't leak across detail views.
    if (this.currentTournament?.id !== tournamentId) {
      this.pendingDetailForm = 'none';
    }
    try {
      const res = await this.apiFetch(`/api/tournament/${encodeURIComponent(tournamentId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.currentTournament = await res.json() as TournamentInfo;
      this.errorMessage = '';
      this.storeTournamentId(tournamentId);
    } catch (err) {
      console.error('[TournamentPanel] Failed to load tournament:', err);
      this.currentTournament = null;
      this.errorMessage = 'Failed to load tournament';
    }
    this.view = 'detail';
    this.render();
  }

  private async loadBracket(tournamentId: string): Promise<void> {
    try {
      const res = await this.apiFetch(`/api/tournament/${encodeURIComponent(tournamentId)}/bracket`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.currentBracket = await res.json() as BracketMatch[][];
      this.errorMessage = '';
    } catch (err) {
      console.error('[TournamentPanel] Failed to load bracket:', err);
      this.currentBracket = null;
      this.errorMessage = 'Failed to load bracket (tournament may not have started)';
    }
    this.view = 'bracket';
    this.render();
  }

  // ── localStorage helpers to remember tournament IDs ──

  private getStoredTournamentIds(): string[] {
    try {
      const raw = localStorage.getItem('counter_pump_tournament_ids');
      return raw ? JSON.parse(raw) as string[] : [];
    } catch {
      return [];
    }
  }

  private storeTournamentId(id: string): void {
    const ids = this.getStoredTournamentIds();
    if (!ids.includes(id)) {
      ids.push(id);
      localStorage.setItem('counter_pump_tournament_ids', JSON.stringify(ids));
    }
  }

  // ── Mutation actions ──

  private async createTournament(name: string, format: TournamentFormat, maxTeams: number): Promise<void> {
    try {
      const res = await this.apiFetch('/api/tournament/create', {
        method: 'POST',
        body: JSON.stringify({ name, format, max_teams: maxTeams }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Create failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Create failed';
        this.render();
        return;
      }
      const tourn = await res.json() as TournamentInfo;
      this.errorMessage = '';
      this.storeTournamentId(tourn.id);
      await this.loadTournamentDetail(tourn.id);
    } catch {
      this.errorMessage = 'Network error creating tournament';
      this.render();
    }
  }

  private async registerTeam(tournamentId: string, teamName: string, playerIds: string[]): Promise<void> {
    try {
      const res = await this.apiFetch('/api/tournament/register', {
        method: 'POST',
        body: JSON.stringify({
          tournament_id: tournamentId,
          team_name: teamName,
          player_ids: playerIds,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Register failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Register failed';
      } else {
        this.errorMessage = 'Team registered!';
      }
    } catch {
      this.errorMessage = 'Network error registering team';
    }
    await this.loadTournamentDetail(tournamentId);
  }

  private async startTournament(tournamentId: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/tournament/start', {
        method: 'POST',
        body: JSON.stringify({ tournament_id: tournamentId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Start failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Start failed';
      } else {
        this.errorMessage = '';
      }
    } catch {
      this.errorMessage = 'Network error starting tournament';
    }
    await this.loadTournamentDetail(tournamentId);
  }

  private async recordResult(
    tournamentId: string,
    round: number,
    matchNum: number,
    score1: number,
    score2: number,
  ): Promise<void> {
    try {
      const res = await this.apiFetch('/api/tournament/result', {
        method: 'POST',
        body: JSON.stringify({
          tournament_id: tournamentId,
          round,
          match_num: matchNum,
          score1,
          score2,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Record failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Record failed';
      } else {
        this.errorMessage = '';
      }
    } catch {
      this.errorMessage = 'Network error recording result';
    }
    await this.loadBracket(tournamentId);
  }

  // ── Rendering ──

  private render(): void {
    // Recycle the render-scope controller so per-render listeners on detached
    // DOM (action buttons, list rows, form fields) are released (UI-M4).
    this.renderController.abort();
    this.renderController = new AbortController();
    this.contentEl.innerHTML = '';

    const header = this.createHeader();
    this.contentEl.appendChild(header);

    // Error bar
    if (this.errorMessage) {
      const errBar = document.createElement('div');
      Object.assign(errBar.style, {
        padding: '8px 16px',
        background: this.errorMessage.includes('registered') || this.errorMessage.includes('!')
          ? 'rgba(76,175,80,0.15)'
          : 'rgba(233,69,96,0.15)',
        color: this.errorMessage.includes('registered') || this.errorMessage.includes('!')
          ? '#4caf50'
          : ACCENT,
        fontSize: '12px',
        letterSpacing: '1px',
      });
      errBar.textContent = this.errorMessage;
      this.contentEl.appendChild(errBar);
    }

    const body = document.createElement('div');
    Object.assign(body.style, {
      flex: '1',
      overflowY: 'auto',
      padding: '16px',
    });

    if (this.view === 'detail' || this.view === 'bracket' || this.view === 'history') {
      this.renderBracketHistoryTabs(body);
    }

    switch (this.view) {
      case 'list':
        this.renderListView(body);
        break;
      case 'create':
        this.renderCreateView(body);
        break;
      case 'detail':
        this.renderDetailView(body);
        break;
      case 'bracket':
        this.renderBracketView(body);
        break;
      case 'history':
        this.renderHistoryView(body);
        break;
    }

    this.contentEl.appendChild(body);

    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '12px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = '[ESC] Close';
    this.contentEl.appendChild(footer);
  }

  private createHeader(): HTMLElement {
    const titles: Record<TournamentView, [string, string]> = {
      list: ['TOURNAMENTS', 'Browse and manage tournaments'],
      create: ['TOURNAMENTS', 'Create a new tournament'],
      detail: ['TOURNAMENTS', this.currentTournament?.name ?? 'Tournament details'],
      bracket: ['BRACKET', this.currentTournament?.name ?? 'Tournament bracket'],
      history: ['MATCH HISTORY', 'Past tournament results'],
    };

    const [title, subtitle] = titles[this.view];

    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '20px 24px 14px',
      borderBottom: `1px solid ${ACCENT}30`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    });

    const left = document.createElement('div');
    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      fontSize: '22px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: ACCENT,
    });
    titleEl.textContent = title;
    left.appendChild(titleEl);

    const subEl = document.createElement('div');
    Object.assign(subEl.style, {
      fontSize: '11px',
      color: '#555',
      letterSpacing: '2px',
      marginTop: '4px',
    });
    subEl.textContent = subtitle;
    left.appendChild(subEl);
    header.appendChild(left);

    const nav = document.createElement('div');
    Object.assign(nav.style, { display: 'flex', gap: '8px' });

    if (this.view !== 'list') {
      const backBtn = this.createBtn('BACK', () => {
        if (this.view === 'bracket' && this.currentTournament) {
          void this.loadTournamentDetail(this.currentTournament.id);
        } else {
          void this.loadTournamentList();
        }
      });
      nav.appendChild(backBtn);
    }

    if (this.view === 'list') {
      const createBtn = this.createBtn('CREATE', () => {
        this.view = 'create';
        this.errorMessage = '';
        this.render();
      });
      createBtn.style.background = 'rgba(233,69,96,0.15)';
      createBtn.style.borderColor = `${ACCENT}60`;
      nav.appendChild(createBtn);

      // U-M-003 follow-up: inline DOM input replaces window.prompt().
      // Form-style row: <input id="..."> + GO button. Enter submits.
      const lookupForm = document.createElement('form');
      Object.assign(lookupForm.style, {
        display: 'inline-flex',
        gap: '6px',
        alignItems: 'center',
      });
      lookupForm.onsubmit = (e) => {
        e.preventDefault();
        const id = lookupInput.value.trim();
        if (id) void this.loadTournamentDetail(id);
      };

      const lookupInput = document.createElement('input');
      lookupInput.type = 'text';
      lookupInput.placeholder = 'tournament id';
      lookupInput.setAttribute('aria-label', 'Tournament ID');
      Object.assign(lookupInput.style, {
        padding: '6px 10px',
        background: 'rgba(0,0,0,0.4)',
        border: `1px solid ${ACCENT}40`,
        color: '#fff',
        fontFamily: FONT,
        fontSize: '12px',
        width: '180px',
      });
      lookupForm.appendChild(lookupInput);

      const lookupBtn = this.createBtn('GO', () => {
        const id = lookupInput.value.trim();
        if (id) void this.loadTournamentDetail(id);
      });
      // Submit through the form's onsubmit too so Enter works.
      lookupBtn.setAttribute('type', 'submit');
      lookupForm.appendChild(lookupBtn);
      nav.appendChild(lookupForm);
    }

    const closeBtn = this.createBtn('CLOSE', () => this.hide());
    nav.appendChild(closeBtn);
    header.appendChild(nav);

    return header;
  }

  private renderBracketHistoryTabs(container: HTMLElement): void {
    const tabs = document.createElement('div');
    Object.assign(tabs.style, {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      paddingBottom: '10px',
    });

    const bracketBtn = this.createBtn('BRACKET', () => {
      if (this.currentTournament) void this.loadBracket(this.currentTournament.id);
    });
    if (!this.currentTournament) {
      bracketBtn.setAttribute('disabled', 'true');
      bracketBtn.style.opacity = '0.45';
      bracketBtn.style.cursor = 'not-allowed';
    }
    if (this.view === 'bracket') {
      bracketBtn.style.background = 'rgba(233,69,96,0.15)';
      bracketBtn.style.borderColor = `${ACCENT}60`;
      bracketBtn.style.color = '#fff';
    }
    tabs.appendChild(bracketBtn);

    const historyBtn = this.createBtn('MATCH HISTORY', () => {
      this.view = 'history';
      this.errorMessage = '';
      this.render();
    });
    if (this.view === 'history') {
      historyBtn.style.background = 'rgba(233,69,96,0.15)';
      historyBtn.style.borderColor = `${ACCENT}60`;
      historyBtn.style.color = '#fff';
    }
    tabs.appendChild(historyBtn);

    container.appendChild(tabs);
  }

  private createBtn(text: string, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    Object.assign(btn.style, {
      padding: '8px 14px',
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: '3px',
      color: '#d2d2cb',
      cursor: 'pointer',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontSize: '11px',
      fontFamily: FONT,
    });
    const sig = this.renderController.signal;
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = `${ACCENT}90`;
      btn.style.color = '#fff';
    }, { signal: sig });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.14)';
      btn.style.color = '#d2d2cb';
    }, { signal: sig });
    btn.addEventListener('click', onClick, { signal: sig });
    return btn;
  }

  private showConfirmDialog(
    title: string,
    message: string,
    onConfirm: () => void,
  ): void {
    this.closeDialog();

    const card = document.createElement('div');
    Object.assign(card.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(420px, calc(100vw - 48px))',
      background: 'rgba(16,16,26,0.98)',
      border: `1px solid ${ACCENT}60`,
      borderRadius: '8px',
      boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
      padding: '18px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      zIndex: '59',
    });

    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      color: '#fff',
      fontSize: '13px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
    });
    titleEl.textContent = title;
    card.appendChild(titleEl);

    const messageEl = document.createElement('div');
    Object.assign(messageEl.style, {
      color: '#bbb',
      fontSize: '12px',
      lineHeight: '1.6',
    });
    messageEl.textContent = message;
    card.appendChild(messageEl);

    const actions = document.createElement('div');
    Object.assign(actions.style, {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    });

    const noBtn = this.createBtn('NO', () => this.closeDialog());
    actions.appendChild(noBtn);

    const yesBtn = this.createBtn('YES', () => {
      this.closeDialog();
      onConfirm();
    });
    yesBtn.style.background = 'rgba(233,69,96,0.15)';
    yesBtn.style.borderColor = `${ACCENT}60`;
    yesBtn.style.color = '#fff';
    actions.appendChild(yesBtn);

    card.appendChild(actions);
    this.overlay.appendChild(card);
    this.activeDialog = card;
  }

  // ── List view ──

  private renderListView(container: HTMLElement): void {
    if (this.tournaments.length === 0) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        textAlign: 'center',
        padding: '40px',
        fontSize: '13px',
      });
      empty.textContent = 'No tournaments tracked. Create one or look up by ID.';
      container.appendChild(empty);
      return;
    }

    // Column headers
    const colHeader = document.createElement('div');
    Object.assign(colHeader.style, {
      display: 'grid',
      gridTemplateColumns: '1fr 120px 80px 100px 80px',
      padding: '8px 12px',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    });
    colHeader.innerHTML = '<div>NAME</div><div>FORMAT</div><div>TEAMS</div><div>STATUS</div><div></div>';
    container.appendChild(colHeader);

    for (const tourn of this.tournaments) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: '1fr 120px 80px 100px 80px',
        padding: '10px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'center',
        fontSize: '13px',
        cursor: 'pointer',
        transition: 'background 0.1s',
      });
      row.addEventListener('mouseenter', () => { row.style.background = 'rgba(255,255,255,0.03)'; }, { signal: this.renderController.signal });
      row.addEventListener('mouseleave', () => { row.style.background = 'transparent'; }, { signal: this.renderController.signal });

      // Name
      const nameEl = document.createElement('div');
      nameEl.style.color = '#fff';
      nameEl.style.fontWeight = 'bold';
      nameEl.textContent = tourn.name;
      row.appendChild(nameEl);

      // Format
      const formatEl = document.createElement('div');
      formatEl.style.color = '#888';
      formatEl.textContent = tourn.format === 'single_elim' ? 'Single Elim' : 'Double Elim';
      row.appendChild(formatEl);

      // Teams
      const teamsEl = document.createElement('div');
      teamsEl.style.color = '#888';
      teamsEl.style.textAlign = 'center';
      teamsEl.textContent = `${tourn.teams?.length ?? 0}/${tourn.max_teams}`;
      row.appendChild(teamsEl);

      // Status badge
      const statusEl = document.createElement('div');
      const statusColor = tourn.status === 'registration' ? '#4caf50'
        : tourn.status === 'in_progress' ? ACCENT
        : '#888';
      statusEl.style.color = statusColor;
      statusEl.style.fontSize = '11px';
      statusEl.style.fontWeight = 'bold';
      statusEl.style.textTransform = 'uppercase';
      statusEl.textContent = tourn.status.replace('_', ' ');
      row.appendChild(statusEl);

      // View button
      const viewBtn = this.createBtn('VIEW', () => {
        void this.loadTournamentDetail(tourn.id);
      });
      viewBtn.style.padding = '4px 10px';
      viewBtn.style.fontSize = '10px';
      row.appendChild(viewBtn);

      container.appendChild(row);
    }
  }

  // ── Create view ──

  private renderCreateView(container: HTMLElement): void {
    const form = document.createElement('div');
    Object.assign(form.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxWidth: '400px',
      margin: '20px auto',
    });

    // Name
    const nameGroup = this.createFormField('Tournament Name', 'text', 'e.g. Weekly Cup #1');
    form.appendChild(nameGroup.wrapper);

    // Format selector
    const formatWrapper = document.createElement('div');
    Object.assign(formatWrapper.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
    const formatLabel = document.createElement('label');
    Object.assign(formatLabel.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    formatLabel.textContent = 'Format';
    formatWrapper.appendChild(formatLabel);

    const formatSelect = document.createElement('select');
    Object.assign(formatSelect.style, {
      padding: '10px 12px',
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: '#fff',
      fontSize: '13px',
      fontFamily: FONT,
      cursor: 'pointer',
      outline: 'none',
    });
    const singleOpt = document.createElement('option');
    singleOpt.value = 'single_elim';
    singleOpt.textContent = 'Single Elimination';
    formatSelect.appendChild(singleOpt);
    const doubleOpt = document.createElement('option');
    doubleOpt.value = 'double_elim';
    doubleOpt.textContent = 'Double Elimination';
    formatSelect.appendChild(doubleOpt);
    formatWrapper.appendChild(formatSelect);
    form.appendChild(formatWrapper);

    // Max teams selector
    const teamsWrapper = document.createElement('div');
    Object.assign(teamsWrapper.style, { display: 'flex', flexDirection: 'column', gap: '6px' });
    const teamsLabel = document.createElement('label');
    Object.assign(teamsLabel.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    teamsLabel.textContent = 'Max Teams';
    teamsWrapper.appendChild(teamsLabel);

    const teamsSelect = document.createElement('select');
    Object.assign(teamsSelect.style, {
      padding: '10px 12px',
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: '#fff',
      fontSize: '13px',
      fontFamily: FONT,
      cursor: 'pointer',
      outline: 'none',
    });
    for (const val of [4, 8, 16]) {
      const opt = document.createElement('option');
      opt.value = String(val);
      opt.textContent = `${val} teams`;
      teamsSelect.appendChild(opt);
    }
    teamsSelect.value = '8';
    teamsWrapper.appendChild(teamsSelect);
    form.appendChild(teamsWrapper);

    // Submit
    const submitBtn = this.createBtn('CREATE TOURNAMENT', () => {
      const name = (nameGroup.input as HTMLInputElement).value.trim();
      const format = formatSelect.value as TournamentFormat;
      const maxTeams = parseInt(teamsSelect.value, 10);
      if (!name) {
        this.errorMessage = 'Tournament name is required';
        this.render();
        return;
      }
      void this.createTournament(name, format, maxTeams);
    });
    submitBtn.style.background = 'rgba(233,69,96,0.15)';
    submitBtn.style.borderColor = `${ACCENT}60`;
    submitBtn.style.padding = '12px 20px';
    submitBtn.style.fontSize = '13px';
    submitBtn.style.alignSelf = 'flex-start';
    form.appendChild(submitBtn);

    container.appendChild(form);
  }

  // ── Detail view ──

  private renderDetailView(container: HTMLElement): void {
    if (!this.currentTournament) {
      const empty = document.createElement('div');
      empty.style.color = '#666';
      empty.style.textAlign = 'center';
      empty.style.padding = '40px';
      empty.textContent = 'Tournament not found';
      container.appendChild(empty);
      return;
    }

    const tourn = this.currentTournament;

    // Info card
    const infoCard = document.createElement('div');
    Object.assign(infoCard.style, {
      padding: '16px',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '5px',
      background: 'rgba(255,255,255,0.03)',
      marginBottom: '16px',
    });

    const tournTitle = document.createElement('div');
    Object.assign(tournTitle.style, {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#fff',
      marginBottom: '6px',
    });
    tournTitle.textContent = tourn.name;
    infoCard.appendChild(tournTitle);

    const meta = document.createElement('div');
    Object.assign(meta.style, { fontSize: '12px', color: '#888', lineHeight: '1.6' });
    const formatLabel = tourn.format === 'single_elim' ? 'Single Elimination' : 'Double Elimination';
    const statusColor = tourn.status === 'registration' ? '#4caf50'
      : tourn.status === 'in_progress' ? ACCENT
      : '#888';
    meta.innerHTML = `
      Format: <span style="color:#fff">${formatLabel}</span><br>
      Teams: <span style="color:#fff">${tourn.teams?.length ?? 0} / ${tourn.max_teams}</span><br>
      Status: <span style="color:${statusColor};font-weight:bold;text-transform:uppercase">${tourn.status.replace('_', ' ')}</span><br>
      Created: <span style="color:#fff">${new Date(tourn.created_at).toLocaleDateString()}</span><br>
      ID: <span style="color:#555;font-size:10px">${tourn.id}</span>
    `;
    infoCard.appendChild(meta);
    container.appendChild(infoCard);

    // Action bar
    const actionBar = document.createElement('div');
    Object.assign(actionBar.style, {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    });

    if (tourn.status === 'registration') {
      // Register team — inline DOM form (U-M-003 admin follow-up).
      const registerBtn = this.createBtn('REGISTER TEAM', () => {
        this.pendingDetailForm = this.pendingDetailForm === 'register-team' ? 'none' : 'register-team';
        this.errorMessage = '';
        this.render();
      });
      if (this.pendingDetailForm === 'register-team') {
        registerBtn.style.background = 'rgba(233,69,96,0.15)';
        registerBtn.style.borderColor = `${ACCENT}60`;
      }
      actionBar.appendChild(registerBtn);

      // Start tournament
      const startBtn = this.createBtn('START TOURNAMENT', () => {
        this.showConfirmDialog(
          'Start Tournament',
          'No more teams can register after this.',
          () => { void this.startTournament(tourn.id); },
        );
      });
      startBtn.style.background = 'rgba(233,69,96,0.15)';
      startBtn.style.borderColor = `${ACCENT}60`;
      actionBar.appendChild(startBtn);
    }

    if (tourn.status === 'in_progress') {
      // View bracket
      const bracketBtn = this.createBtn('VIEW BRACKET', () => {
        void this.loadBracket(tourn.id);
      });
      bracketBtn.style.background = 'rgba(233,69,96,0.15)';
      bracketBtn.style.borderColor = `${ACCENT}60`;
      actionBar.appendChild(bracketBtn);

      // Record result — inline DOM form (U-M-003 admin follow-up).
      const resultBtn = this.createBtn('RECORD RESULT', () => {
        this.pendingDetailForm = this.pendingDetailForm === 'record-result' ? 'none' : 'record-result';
        this.errorMessage = '';
        this.render();
      });
      if (this.pendingDetailForm === 'record-result') {
        resultBtn.style.background = 'rgba(233,69,96,0.15)';
        resultBtn.style.borderColor = `${ACCENT}60`;
      }
      actionBar.appendChild(resultBtn);
    }

    if (tourn.status === 'completed') {
      const bracketBtn = this.createBtn('VIEW FINAL BRACKET', () => {
        void this.loadBracket(tourn.id);
      });
      bracketBtn.style.background = 'rgba(233,69,96,0.15)';
      bracketBtn.style.borderColor = `${ACCENT}60`;
      actionBar.appendChild(bracketBtn);
    }

    // Refresh
    const refreshBtn = this.createBtn('REFRESH', () => {
      void this.loadTournamentDetail(tourn.id);
    });
    actionBar.appendChild(refreshBtn);

    container.appendChild(actionBar);

    // Inline admin form (U-M-003 follow-up): renders between the action bar
    // and the teams list when the user has clicked REGISTER TEAM or
    // RECORD RESULT. Replaces window.prompt chains with a DOM form.
    if (this.pendingDetailForm === 'register-team' && tourn.status === 'registration') {
      this.renderRegisterTeamForm(container, tourn.id);
    } else if (this.pendingDetailForm === 'record-result' && tourn.status === 'in_progress') {
      this.renderRecordResultForm(container, tourn.id);
    }

    // Teams list
    const teamsHeader = document.createElement('div');
    Object.assign(teamsHeader.style, {
      fontSize: '14px',
      fontWeight: 'bold',
      color: ACCENT,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: '8px',
    });
    teamsHeader.textContent = `REGISTERED TEAMS (${tourn.teams?.length ?? 0})`;
    container.appendChild(teamsHeader);

    if (!tourn.teams || tourn.teams.length === 0) {
      const empty = document.createElement('div');
      empty.style.color = '#666';
      empty.style.padding = '20px';
      empty.style.textAlign = 'center';
      empty.textContent = 'No teams registered yet';
      container.appendChild(empty);
    } else {
      // Column headers
      const colHeader = document.createElement('div');
      Object.assign(colHeader.style, {
        display: 'grid',
        gridTemplateColumns: MOBILE_FRIENDLY_4COL_GRID,
        padding: '8px 12px',
        fontSize: '10px',
        color: '#666',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      });
      colHeader.innerHTML = '<div>SEED</div><div>TEAM</div><div>PLAYERS</div><div>SIZE</div>';
      container.appendChild(colHeader);

      for (const team of tourn.teams) {
        const row = document.createElement('div');
        Object.assign(row.style, {
          display: 'grid',
          gridTemplateColumns: MOBILE_FRIENDLY_4COL_GRID,
          padding: '8px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          alignItems: 'center',
          fontSize: '13px',
        });

        const seedEl = document.createElement('div');
        seedEl.style.color = '#888';
        seedEl.textContent = `#${team.seed}`;
        row.appendChild(seedEl);

        const nameEl = document.createElement('div');
        nameEl.style.color = '#fff';
        nameEl.style.fontWeight = 'bold';
        nameEl.textContent = team.name;
        row.appendChild(nameEl);

        const playersEl = document.createElement('div');
        playersEl.style.color = '#666';
        playersEl.style.fontSize = '11px';
        playersEl.textContent = team.player_ids.map(id =>
          id.length > 8 ? id.slice(0, 6) + '..' : id
        ).join(', ');
        row.appendChild(playersEl);

        const sizeEl = document.createElement('div');
        sizeEl.style.color = '#888';
        sizeEl.style.textAlign = 'center';
        sizeEl.textContent = `${team.player_ids.length}`;
        row.appendChild(sizeEl);

        container.appendChild(row);
      }
    }
  }

  // ── Bracket view (tree visualization) ──

  private renderBracketView(container: HTMLElement): void {
    if (!this.currentBracket || this.currentBracket.length === 0) {
      const empty = document.createElement('div');
      empty.style.color = '#666';
      empty.style.textAlign = 'center';
      empty.style.padding = '40px';
      empty.textContent = 'No bracket data available';
      container.appendChild(empty);
      return;
    }

    // Build a team name lookup
    const teamNames: Record<string, string> = {};
    if (this.currentTournament?.teams) {
      for (const t of this.currentTournament.teams) {
        teamNames[t.id] = t.name;
      }
    }

    // Scrollable bracket container
    const bracketShell = document.createElement('div');
    Object.assign(bracketShell.style, {
      position: 'relative',
      overflowX: 'auto',
    });

    const bracketWrap = document.createElement('div');
    Object.assign(bracketWrap.style, {
      display: 'flex',
      gap: '32px',
      padding: '8px 0',
      touchAction: 'pan-x',
      transform: `scale(${this.bracketZoom})`,
      transformOrigin: 'top left',
      transition: 'transform 0.15s ease-out',
      width: 'max-content',
    });

    if (isMobile()) {
      bracketWrap.style.paddingTop = '44px';
      const zoomControls = document.createElement('div');
      Object.assign(zoomControls.style, {
        position: 'absolute',
        top: '0',
        right: '0',
        display: 'flex',
        gap: '6px',
        zIndex: '1',
      });

      const setZoom = (zoom: number) => {
        this.bracketZoom = zoom;
        bracketWrap.style.transform = `scale(${this.bracketZoom})`;
      };

      const zoomOut = this.createBtn('-', () => setZoom(0.7));
      zoomOut.setAttribute('aria-label', 'Zoom bracket out');
      zoomOut.style.padding = '6px 10px';
      const zoomReset = this.createBtn('1x', () => setZoom(1));
      zoomReset.setAttribute('aria-label', 'Reset bracket zoom');
      zoomReset.style.padding = '6px 10px';
      const zoomIn = this.createBtn('+', () => setZoom(1.3));
      zoomIn.setAttribute('aria-label', 'Zoom bracket in');
      zoomIn.style.padding = '6px 10px';
      zoomControls.appendChild(zoomOut);
      zoomControls.appendChild(zoomReset);
      zoomControls.appendChild(zoomIn);
      bracketShell.appendChild(zoomControls);
    }

    for (let roundIdx = 0; roundIdx < this.currentBracket.length; roundIdx++) {
      const round = this.currentBracket[roundIdx];
      const roundCol = document.createElement('div');
      Object.assign(roundCol.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        minWidth: '200px',
      });

      // Round label
      const roundLabel = document.createElement('div');
      Object.assign(roundLabel.style, {
        fontSize: '11px',
        color: ACCENT,
        letterSpacing: '2px',
        textTransform: 'uppercase',
        textAlign: 'center',
        marginBottom: '4px',
        fontWeight: 'bold',
      });
      const isLast = roundIdx === this.currentBracket.length - 1;
      roundLabel.textContent = isLast ? 'FINAL' : `ROUND ${roundIdx + 1}`;
      roundCol.appendChild(roundLabel);

      for (const match of round) {
        const matchCard = document.createElement('div');
        Object.assign(matchCard.style, {
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '4px',
          background: 'rgba(255,255,255,0.02)',
          overflow: 'hidden',
        });

        // Team 1
        const team1 = this.createBracketTeamRow(
          match.team1_id,
          teamNames[match.team1_id] ?? (match.team1_id || 'TBD'),
          match.score1,
          match.winner_id === match.team1_id && match.status === 'completed',
        );
        matchCard.appendChild(team1);

        // Divider
        const divider = document.createElement('div');
        divider.style.borderTop = '1px solid rgba(255,255,255,0.06)';
        matchCard.appendChild(divider);

        // Team 2
        const team2 = this.createBracketTeamRow(
          match.team2_id,
          teamNames[match.team2_id] ?? (match.team2_id || 'TBD'),
          match.score2,
          match.winner_id === match.team2_id && match.status === 'completed',
        );
        matchCard.appendChild(team2);

        // Status indicator
        if (match.status === 'completed') {
          const statusBar = document.createElement('div');
          Object.assign(statusBar.style, {
            padding: '3px 8px',
            fontSize: '9px',
            color: '#4caf50',
            textAlign: 'center',
            letterSpacing: '1px',
            background: 'rgba(76,175,80,0.08)',
          });
          statusBar.textContent = 'COMPLETED';
          matchCard.appendChild(statusBar);
        }

        roundCol.appendChild(matchCard);
      }

      bracketWrap.appendChild(roundCol);
    }

    bracketShell.appendChild(bracketWrap);
    container.appendChild(bracketShell);
  }

  private renderHistoryView(container: HTMLElement): void {
    if (this.matchHistory.length === 0) {
      const empty = document.createElement('div');
      empty.style.color = '#666';
      empty.style.textAlign = 'center';
      empty.style.padding = '40px';
      empty.textContent = 'No match history yet.';
      container.appendChild(empty);
      return;
    }

    const colHeader = document.createElement('div');
    Object.assign(colHeader.style, {
      display: 'grid',
      gridTemplateColumns: '130px 1fr 130px',
      padding: '8px 12px',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    });
    colHeader.innerHTML = '<div>DATE</div><div>TOURNAMENT</div><div>PLACEMENT</div>';
    container.appendChild(colHeader);

    for (const entry of this.matchHistory) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: '130px 1fr 130px',
        padding: '10px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'center',
        fontSize: '13px',
      });

      const date = new Date(entry.date);
      const dateEl = document.createElement('div');
      dateEl.style.color = '#888';
      dateEl.textContent = Number.isNaN(date.getTime()) ? entry.date : date.toLocaleDateString();
      row.appendChild(dateEl);

      const nameEl = document.createElement('div');
      nameEl.style.color = '#fff';
      nameEl.style.fontWeight = 'bold';
      nameEl.textContent = entry.name;
      row.appendChild(nameEl);

      const placementEl = document.createElement('div');
      placementEl.style.color = ACCENT;
      placementEl.style.fontWeight = 'bold';
      placementEl.textContent = entry.placement;
      row.appendChild(placementEl);

      container.appendChild(row);
    }
  }

  private createBracketTeamRow(
    teamId: string,
    teamName: string,
    score: number,
    isWinner: boolean,
  ): HTMLElement {
    const row = document.createElement('div');
    Object.assign(row.style, {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      background: isWinner ? 'rgba(76,175,80,0.08)' : 'transparent',
    });

    const nameEl = document.createElement('div');
    nameEl.style.color = teamId ? (isWinner ? '#4caf50' : '#ccc') : '#555';
    nameEl.style.fontSize = '12px';
    nameEl.style.fontWeight = isWinner ? 'bold' : 'normal';
    nameEl.textContent = teamName;
    row.appendChild(nameEl);

    const scoreEl = document.createElement('div');
    scoreEl.style.color = isWinner ? '#4caf50' : '#888';
    scoreEl.style.fontWeight = 'bold';
    scoreEl.style.fontSize = '14px';
    scoreEl.textContent = teamId ? String(score) : '-';
    row.appendChild(scoreEl);

    return row;
  }

  // ── Form helpers ──

  private createFormField(
    label: string,
    type: string,
    placeholder: string,
  ): { wrapper: HTMLElement; input: HTMLInputElement } {
    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, { display: 'flex', flexDirection: 'column', gap: '6px' });

    const labelEl = document.createElement('label');
    Object.assign(labelEl.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    labelEl.textContent = label;
    wrapper.appendChild(labelEl);

    const input = document.createElement('input');
    input.type = type;
    input.placeholder = placeholder;
    Object.assign(input.style, {
      padding: '10px 12px',
      minHeight: isMobile() ? '44px' : '0',
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: '#fff',
      fontSize: '13px',
      fontFamily: FONT,
      outline: 'none',
    });
    input.addEventListener('focus', () => { input.style.borderColor = `${ACCENT}60`; }, { signal: this.renderController.signal });
    input.addEventListener('blur', () => { input.style.borderColor = 'rgba(255,255,255,0.12)'; }, { signal: this.renderController.signal });
    wrapper.appendChild(input);

    return { wrapper, input };
  }

  /**
   * Inline form replacing the REGISTER TEAM window.prompt() chain.
   * U-M-003 admin follow-up.
   */
  private renderRegisterTeamForm(container: HTMLElement, tournamentId: string): void {
    const form = document.createElement('form');
    Object.assign(form.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '14px',
      marginBottom: '16px',
      border: `1px solid ${ACCENT}40`,
      borderRadius: '4px',
      background: 'rgba(233,69,96,0.04)',
    });

    const formTitle = document.createElement('div');
    Object.assign(formTitle.style, {
      fontSize: '12px',
      color: ACCENT,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
    });
    formTitle.textContent = 'Register Team';
    form.appendChild(formTitle);

    const nameField = this.createFormField('Team Name', 'text', 'e.g. Alpha Squad');
    form.appendChild(nameField.wrapper);

    const idsField = this.createFormField('Player IDs (comma-separated)', 'text', 'p1, p2, p3');
    form.appendChild(idsField.wrapper);

    const buttonRow = document.createElement('div');
    Object.assign(buttonRow.style, { display: 'flex', gap: '8px' });
    if (isMobile()) {
      Object.assign(form.style, {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      });
      Object.assign(buttonRow.style, {
        flexDirection: 'column',
        alignItems: 'stretch',
      });
      nameField.wrapper.style.width = '100%';
      idsField.wrapper.style.width = '100%';
    }

    const submitBtn = this.createBtn('SUBMIT', () => {
      const teamName = nameField.input.value.trim();
      if (!teamName) {
        this.errorMessage = 'Team name is required';
        this.render();
        return;
      }
      const playerIds = idsField.input.value.split(',').map((s) => s.trim()).filter(Boolean);
      if (playerIds.length === 0) {
        this.errorMessage = 'At least one player ID is required';
        this.render();
        return;
      }
      void this.registerTeam(tournamentId, teamName, playerIds);
      this.pendingDetailForm = 'none';
    });
    submitBtn.setAttribute('type', 'submit');
    submitBtn.style.background = 'rgba(233,69,96,0.15)';
    submitBtn.style.borderColor = `${ACCENT}60`;
    if (isMobile()) submitBtn.style.minHeight = '44px';
    buttonRow.appendChild(submitBtn);

    const cancelBtn = this.createBtn('CANCEL', () => {
      this.pendingDetailForm = 'none';
      this.errorMessage = '';
      this.render();
    });
    if (isMobile()) cancelBtn.style.minHeight = '44px';
    buttonRow.appendChild(cancelBtn);

    form.appendChild(buttonRow);

    // Enter on either input submits via the form's onsubmit.
    form.onsubmit = (e) => {
      e.preventDefault();
      submitBtn.click();
    };

    container.appendChild(form);
  }

  /**
   * Inline form replacing the RECORD RESULT window.prompt() chain.
   * U-M-003 admin follow-up.
   */
  private renderRecordResultForm(container: HTMLElement, tournamentId: string): void {
    const form = document.createElement('form');
    Object.assign(form.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '14px',
      marginBottom: '16px',
      border: `1px solid ${ACCENT}40`,
      borderRadius: '4px',
      background: 'rgba(233,69,96,0.04)',
    });

    const formTitle = document.createElement('div');
    Object.assign(formTitle.style, {
      fontSize: '12px',
      color: ACCENT,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
    });
    formTitle.textContent = 'Record Match Result';
    form.appendChild(formTitle);

    const roundField = this.createFormField('Round (0-indexed)', 'number', '0');
    form.appendChild(roundField.wrapper);

    const matchField = this.createFormField('Match # in round (0-indexed)', 'number', '0');
    form.appendChild(matchField.wrapper);

    const s1Field = this.createFormField('Team 1 Score', 'number', '16');
    form.appendChild(s1Field.wrapper);

    const s2Field = this.createFormField('Team 2 Score', 'number', '12');
    form.appendChild(s2Field.wrapper);

    const buttonRow = document.createElement('div');
    Object.assign(buttonRow.style, { display: 'flex', gap: '8px' });

    const submitBtn = this.createBtn('SUBMIT', () => {
      const round = parseInt(roundField.input.value, 10);
      const matchNum = parseInt(matchField.input.value, 10);
      const score1 = parseInt(s1Field.input.value, 10);
      const score2 = parseInt(s2Field.input.value, 10);
      if ([round, matchNum, score1, score2].some(isNaN)) {
        this.errorMessage = 'All fields must be numbers';
        this.render();
        return;
      }
      if (round < 0 || matchNum < 0 || score1 < 0 || score2 < 0) {
        this.errorMessage = 'Negative values not allowed';
        this.render();
        return;
      }
      void this.recordResult(tournamentId, round, matchNum, score1, score2);
      this.pendingDetailForm = 'none';
    });
    submitBtn.setAttribute('type', 'submit');
    submitBtn.style.background = 'rgba(233,69,96,0.15)';
    submitBtn.style.borderColor = `${ACCENT}60`;
    buttonRow.appendChild(submitBtn);

    const cancelBtn = this.createBtn('CANCEL', () => {
      this.pendingDetailForm = 'none';
      this.errorMessage = '';
      this.render();
    });
    buttonRow.appendChild(cancelBtn);

    form.appendChild(buttonRow);

    form.onsubmit = (e) => {
      e.preventDefault();
      submitBtn.click();
    };

    container.appendChild(form);
  }
}
